<script>
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { 
    faUsers,
    faUserCheck,
    faUserXmark,
    faChartLine
  } from '@fortawesome/pro-light-svg-icons';
  import { isOlderThanHours } from '$utils/dateUtils.js';
  import { isCompactMode } from '$stores/uiStore';

  export let persons = [];
  export let studyProperties = null;
  // When true, every card body shows "Calculating…" instead of a number.
  // Set by the parent page while the bulk /persons?pageSize=999999 fetch is
  // still in flight — without this, the cards briefly render `0` (because
  // persons.length === 0 on initial mount) which is misleading. The parent
  // flips this to false in both success and error branches so the UI never
  // gets stuck in the loading state.
  export let loading = false;

  // Default completed status codes if not defined in study settings
  const DEFAULT_COMPLETED_STATUS_CODES = ['COMPD'];

  // Get completed status codes from study settings or use default
  $: completedStatusCodes = studyProperties?.completedStatusCodes?.length > 0
    ? studyProperties.completedStatusCodes
    : DEFAULT_COMPLETED_STATUS_CODES;

  // Calculate statistics
  $: totalCount = persons.length;
  $: enrolledCount = persons.filter(p => p.statusCode === 'ENRO').length;
  $: failedCount = persons.filter(p => p.statusCode === 'FAILED').length;
  $: completeCount = persons.filter(p => completedStatusCodes.includes(p.statusCode)).length;
  $: activeCGMCount = persons.filter(p => p.latestReadingDate && !isOlderThanHours(p.latestReadingDate, 24)).length;
</script>

<div class="grid grid-cols-4 gap-4 px-1 mt-3">
  <!-- Total Participants Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-blue-500">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faUsers} class="text-xl text-blue-500 flex-shrink-0" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Total Participants</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if loading}<span class="text-sm font-normal italic text-gray-500 dark:text-gray-400">Calculating…</span>{:else}{totalCount}{/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">All participants in site</p>
    {/if}
  </div>

  <!-- Enrolled Participants Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-green-500">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faUserCheck} class="text-xl text-green-500 flex-shrink-0" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Active</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if loading}<span class="text-sm font-normal italic text-gray-500 dark:text-gray-400">Calculating…</span>{:else}{enrolledCount}{/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Actively on the study</p>
    {/if}
  </div>

  <!-- Failed Participants Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-red-500">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faUserXmark} class="text-xl text-red-500 flex-shrink-0" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">End of Participation</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if loading}<span class="text-sm font-normal italic text-gray-500 dark:text-gray-400">Calculating…</span>{:else}{completeCount}{/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Ended participation</p>
    {/if}
  </div>

  <!-- Active Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-purple-500">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faChartLine} class="text-xl text-purple-500 flex-shrink-0" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Measurements</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if loading}<span class="text-sm font-normal italic text-gray-500 dark:text-gray-400">Calculating…</span>{:else}{activeCGMCount}{/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Measurements in last 24 hours</p>
    {/if}
  </div>
</div> 